import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { routes } from './app.routes';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { API_BASE_URL } from './shared/api-config';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/aura';
import {
  MsalGuard,
  MsalService,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MsalBroadcastService,
  MsalInterceptor,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import * as Sentry from '@sentry/angular';
import { silentMsalInterceptor } from './shared/interceptors/silent-msal.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: API_BASE_URL, useValue: environment.apiUrl },
    // GC - This is needed for the MsalInterceptor. See the note below on why MsalInterceptor is not used.
    //provideHttpClient(withInterceptorsFromDi()),
    provideHttpClient(withInterceptors([silentMsalInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    // GC - I don't want to use the MsalInterceptor. If it fails to acquire a token silently, it tries interaction.
    // Redirect is not supported for the app, and popup gets blocked by browsers (since it's not a user action).
    //{ provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigurationFactory,
    },
    MsalService,
    provideAppInitializer(() => inject(MsalService).initialize()),
    MsalBroadcastService,
    MsalGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    provideNgxMask(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};

export function MSALInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: 'e1d03e3f-4d71-46fb-95b2-1df58176699b',
      authority: 'https://vanhutechclients.ciamlogin.com/',
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.postLogoutRedirectUri,
      //knownAuthorities: ['vanhu-tech.b2clogin.com'],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback(logLevel: LogLevel, message: string) {
          console.debug(message);
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

// GC - I don't want to use the MsalInterceptor. If it fails to acquire a token silently, it tries interaction.
// Redirect is not supported for the app, and popup gets blocked by browsers (since it's not a user action).
//
// export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
//   return {
//     interactionType: InteractionType.Redirect,
//     protectedResourceMap: new Map([
//       ['https://localhost:7256/api', environment.scopes],
//       ['https://app.vanhutech.org/api', environment.scopes],
//       [
//         'https://vanhu-tech-f4gpcpesfke9c7hy.southafricanorth-01.azurewebsites.net/api',
//         environment.scopes,
//       ],
//     ]),
//   };
// }

export function MsalGuardConfigurationFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: environment.scopes,
    },
    loginFailedRoute: '/',
  };
}
